// Dark-mode store (zustand). Persists choice in localStorage and toggles the
// `dark` class on <html> so Tailwind's `dark:` variants apply. Defaults to the
// OS preference on first load.

import { create } from "zustand";

type Theme = "light" | "dark";
const STORAGE_KEY = "rm_theme";

function initialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

interface ThemeState {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initialTheme(),
  toggle: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
  setTheme: (t) => {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, t);
    applyTheme(t);
    set({ theme: t });
  },
}));

/** Apply the persisted theme to <html> as early as possible (call from main.tsx). */
export function bootstrapTheme(): void {
  applyTheme(useThemeStore.getState().theme);
}
